import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppNav } from '../../components/layout/AppNav';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { offersService, type Offer } from '../../services/offers.service';
import { useToast } from '../../components/ui/Toast';
import { formatSAR } from '../../utils/formatters';
import { CheckCircle2, Send } from 'lucide-react';

export const OfferGeneratedPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [offer, setOffer] = useState<Offer | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    offersService.get(id).then(setOffer).catch(() => showToast('Failed to load offer', 'error')).finally(() => setLoading(false));
  }, [id, showToast]);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <AppNav title="Offer Generated" />
      <main style={{ flex: 1, padding: '24px', overflowY: 'auto', display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%', maxWidth: '560px', paddingTop: '40px' }}>
            {[1, 2].map((i) => <div key={i} className="skeleton" style={{ height: '80px', borderRadius: '10px' }} />)}
          </div>
        ) : (
          <div className="card" style={{ maxWidth: '560px', width: '100%', textAlign: 'center', padding: '40px', marginTop: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
              <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'var(--succ-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CheckCircle2 size={32} color="var(--succ)" />
              </div>
            </div>
            <h2 style={{ fontSize: '22px', fontWeight: 700, color: 'var(--text-1)', marginBottom: '8px' }}>
              Offer {offer?.offerNumber} is ready!
            </h2>
            <p style={{ color: 'var(--text-2)', marginBottom: '28px', fontSize: '13.5px', lineHeight: 1.6 }}>
              The offer has been created successfully. Send it to the customer now or save it for later.
            </p>

            {offer && (
              <div style={{ background: 'var(--surface-alt)', borderRadius: '10px', padding: '16px', marginBottom: '24px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '13.5px', textAlign: 'left' }}>
                <div><span style={{ color: 'var(--text-2)' }}>Status: </span><Badge label={offer.status} /></div>
                <div><span style={{ color: 'var(--text-2)' }}>Total: </span><strong style={{ color: 'var(--p)' }}>{formatSAR(offer.total)}</strong></div>
                <div><span style={{ color: 'var(--text-2)' }}>Customer: </span>{offer.customer?.nameEn ?? '—'}</div>
                <div><span style={{ color: 'var(--text-2)' }}>Items: </span>{offer.items?.length ?? 0}</div>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <Button style={{ width: '100%', justifyContent: 'center' }} onClick={() => navigate(`/offers/${id}/send`)}>
                <Send size={14} />
                Send to Customer Now
              </Button>
              <Button variant="ghost" style={{ width: '100%', justifyContent: 'center' }} onClick={() => navigate('/my-offers')}>
                Save for Later
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
